import { useCallback, useRef, useState } from 'react';
import api from '@helpers/api';

export default function useIntegrationLogs() {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ pages: 0, records: 0, page: 1 });
    const [selectedLog, setSelectedLog] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');

    const integrationRef = useRef(null);
    const statusFilterRef = useRef('');

    const handleLoadLogs = useCallback(async (page = 1, status = '') => {
        if (!integrationRef.current?.id) return;
        setLoading(true);
        setSelectedLog(null);
        try {
            const params = { page, per_page: 15 };
            if (status) params.status = status;
            const res = await api.get(`integration/${integrationRef.current.id}/logs`, params);
            if (res?.response?.status >= 400) {
                setLogs([]);
                setPagination({ pages: 0, records: 0, page: 1 });
            } else {
                const raw = res?.data;
                const list = Array.isArray(raw) ? raw : raw?.data ?? [];
                const total = raw?.total ?? list.length;
                const perPage = raw?.per_page ?? 1;
                
                setLogs(list);
                setPagination({
                    pages: raw?.last_page ?? Math.ceil(total / perPage),
                    records: total,
                    page: raw?.current_page ?? page,
                });
                if (list.length > 0) setSelectedLog(list[0]);
            }
        } catch (e) {
            console.error(e);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const openModal = useCallback((integration) => {
        integrationRef.current = integration;
        statusFilterRef.current = '';
        setStatusFilter('');
        setSelectedLog(null);
        setLogs([]);
        setPagination({ pages: 0, records: 0, page: 1 });
        setOpen(true);
        handleLoadLogs(1, '');
    }, [handleLoadLogs]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setSelectedLog(null);
        setLogs([]);
    }, []);

    const handleFilterChange = useCallback((status) => {
        statusFilterRef.current = status;
        setStatusFilter(status);
        handleLoadLogs(1, status);
    }, [handleLoadLogs]);

    const handlePageChange = useCallback((page) => {
        handleLoadLogs(page, statusFilterRef.current);
    }, [handleLoadLogs]);

    const handleSelectLog = useCallback((log) => {
        setSelectedLog(log);
    }, []);

    return {
        open,
        loading,
        logs,
        pagination,
        selectedLog,
        statusFilter,
        integration: integrationRef,
        openModal,
        handleClose,
        handleFilterChange,
        handlePageChange,
        handleSelectLog,
    };
}
